import {
  documentListMaxHeight,
  useDocumentListHeightLimit,
} from "@/domains/documents/components/organisms/documentListHeightLimit";
import { useDocumentFiltersOptional } from "@/domains/documents/context/DocumentFiltersContext";
import type { SortKey } from "@/domains/documents/utils/documentSorting";
import {
  DEFAULT_NAME_MIN_WIDTH,
  fitDocumentColumns,
  type OptionalColumn,
} from "@/domains/documents/utils/fitDocumentColumns";
import { useFillToBottomHeight } from "@/hooks/useFillToBottomHeight";
import { useCallback, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

import type { documentTableOptions } from "./documentTableOptions";
import type { useTableRows } from "./useTableRows";
type Options = Pick<ReturnType<typeof useTableRows>, "flattenedItems"> &
  Pick<
    ReturnType<typeof documentTableOptions>,
    | "showCheckbox"
    | "requestedShowDateCreated"
    | "showUser"
    | "requestedShowVersion"
    | "requestedShowStatus"
    | "requestedShowTags"
    | "requestedShowParent"
    | "showActions"
    | "currentPage"
  >;
export function useTableSizing(options: Options) {
  const { flattenedItems, currentPage } = options;
  // Ref for the scroll container (virtual scrolling)
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  // Fills the window instead of the old `calc(100vh - 280px)`, which stopped
  // the table short and left a blank band under the last row. The virtualizer
  // needs the node imperatively, so this callback feeds both it and the
  // measurement — the scroll container only mounts once the list is non-empty.
  const { measureRef, maxHeight: filledMaxHeight } = useFillToBottomHeight();
  // A host with content after the list (the Brief page's Appendix) trims it.
  const heightLimit = useDocumentListHeightLimit();
  const scrollMaxHeight = documentListMaxHeight(filledMaxHeight, heightLimit);
  const setScrollContainer = useCallback(
    (node: HTMLDivElement | null) => {
      scrollContainerRef.current = node;
      measureRef(node);
    },
    [measureRef]
  );

  // The table never scrolls sideways: optional columns drop, least useful
  // first, until the rest fit the width the rows actually get — the scroll
  // container's client width, so a vertical scrollbar is accounted for. It
  // mounts only once the list has rows, hence the re-run on `hasRows`.
  const tableWrapperRef = useRef<HTMLDivElement>(null);
  const [rowArea, setRowArea] = useState({ width: 0, remPx: 16 });
  const hasRows = flattenedItems.length > 0;
  useEffect(() => {
    const wrapper = tableWrapperRef.current;
    if (!wrapper || typeof ResizeObserver === "undefined") return;
    const measure = () => {
      const width = scrollContainerRef.current?.clientWidth || wrapper.clientWidth;
      const remPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      setRowArea((prev) =>
        prev.width === width && prev.remPx === remPx ? prev : { width, remPx }
      );
    };
    // Refit before the browser paints the new width. A plain state update
    // renders after that paint, so for a frame the rows keep the columns
    // fitted to the old width — on a narrowing (the Brief page's suggestions
    // opening) wider than the rows, and the list flashes horizontal
    // scrollbars. A ResizeObserver callback runs between layout and paint, and
    // the layout a flushed render changes is redone before that paint.
    const observer = new ResizeObserver(() => flushSync(measure));
    observer.observe(wrapper);
    if (scrollContainerRef.current) observer.observe(scrollContainerRef.current);
    measure();
    return () => observer.disconnect();
  }, [hasRows]);
  // A list sorted by Uploader keeps that column: ordered by a column that is
  // not on screen, it has no arrow to read or undo the order by. (The date is
  // not pinned this way — it is the default sort, so pinning it would squeeze
  // the filename on every phone.)
  const sortKey = useDocumentFiltersOptional()?.filters.sortKey;
  const fittedColumns = fittedTableColumns(options, rowArea, sortedColumn(sortKey));
  const { showDateCreated, showUser, showVersion, showStatus, showTags, showParent } =
    fittedColumns.flags;

  // Scroll to top when page changes
  useEffect(() => {
    if (currentPage !== undefined) {
      scrollContainerRef.current?.scrollTo({ top: 0 });
    }
  }, [currentPage]);
  return {
    scrollContainerRef,
    rowArea,
    showStatus,
    showTags,
    showParent,
    tableWrapperRef,
    fittedColumns,
    showVersion,
    showDateCreated,
    // Fitted like the others: it overrides the requested flag of the same name.
    showUser,
    // The host asked for an Uploader column and the width took it away: the
    // date's tooltip names the uploader instead. A host that turns the column
    // off (the share page passes `showUser={false}`) gets no name there.
    uploaderInDateHint: !!options.showUser && !showUser,
    // No size column: a list sorted by size names it beside the filename.
    sizeInName: sortKey === "size",
    setScrollContainer,
    scrollMaxHeight,
    heightLimit,
  };
}

function sortedColumn(sortKey: SortKey | undefined): OptionalColumn | null {
  return sortKey === "user" ? "showUser" : null;
}

function fittedTableColumns(
  options: Options,
  rowArea: { width: number; remPx: number },
  keep: OptionalColumn | null
) {
  const {
    showCheckbox,
    requestedShowDateCreated,
    showUser,
    requestedShowVersion,
    requestedShowStatus,
    requestedShowTags,
    requestedShowParent,
    showActions,
  } = options;
  const fittedColumns = fitDocumentColumns(
    {
      showCheckbox: !!showCheckbox,
      showDateCreated: !!requestedShowDateCreated,
      showUser: !!showUser,
      showVersion: !!requestedShowVersion,
      showStatus: !!requestedShowStatus,
      showTags: !!requestedShowTags,
      showParent: !!requestedShowParent,
      showActions: !!showActions,
    },
    rowArea.width,
    DEFAULT_NAME_MIN_WIDTH,
    rowArea.remPx,
    keep
  );
  return fittedColumns;
}
